import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Youtube, ArrowUpRight, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNEL_ID = "UCxvmL3w8S2e6tTPN18sE5Fg";
const SHORTS_URL = `https://www.youtube.com/channel/${CHANNEL_ID}/shorts`;

const FALLBACK_VIDEO_ID = "in20_cl-j3c";

function buildEmbedSrc(videoId) {
  // enablejsapi=1 so we can toggle mute via postMessage
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&enablejsapi=1&mute=1&autoplay=1&playsinline=1&loop=1&playlist=${videoId}`;
}

export default function YouTubeShorts() {
  const [videoId, setVideoId] = useState(FALLBACK_VIDEO_ID);
  const [isMuted, setIsMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/latest-video?kind=short`)
      .then((r) => {
        const id = r?.data?.video_id;
        if (!cancelled && id && /^[A-Za-z0-9_-]{11}$/.test(id)) {
          setVideoId(id);
          setLoaded(true);
        }
      })
      .catch(() => {
        /* silently fall back */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMute = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    const cmd = isMuted ? "unMute" : "mute";
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: cmd, args: [] }),
      "*",
    );
    setIsMuted((m) => !m);
  };

  const embed = buildEmbedSrc(videoId);

  return (
    <section
      id="shorts"
      data-testid="youtube-shorts-section"
      className="py-20 md:py-28 px-6 md:px-12 bg-black"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold" />
            <span className="text-xs uppercase tracking-overline text-zinc-500">
              02 — Moments from the Road
            </span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            Quick takes,{" "}
            <em className="italic text-gold">vertical and unfiltered</em>.
          </h2>
          <p className="mt-6 max-w-xl text-base md:text-lg text-zinc-400 leading-[1.65] font-light">
            The fastest cut of our journey — a daily-ish drop of YouTube Shorts
            from the road. Auto-updated as we publish.
          </p>
        </div>

        {/* Vertical 9:16 player, centered */}
        <div className="reveal flex justify-center">
          <div
            className="relative bg-black border border-white/10 overflow-hidden w-full max-w-[360px] md:max-w-[400px]"
            style={{ aspectRatio: "9 / 16" }}
          >
            <iframe
              ref={iframeRef}
              key={videoId}
              src={embed}
              title="Latest YouTube Short"
              data-testid="yt-shorts-iframe"
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />

            {/* Floating mute/unmute toggle */}
            <button
              type="button"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute short" : "Mute short"}
              aria-pressed={!isMuted}
              data-testid="yt-shorts-mute-toggle"
              className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 px-3.5 py-2 bg-black/70 backdrop-blur-md border border-white/15 hover:border-gold hover:text-gold text-zinc-100 text-[11px] uppercase tracking-overline transition-colors duration-300"
            >
              {isMuted ? (
                <>
                  <VolumeX className="w-4 h-4" strokeWidth={1.75} />
                  Unmute
                </>
              ) : (
                <>
                  <Volume2 className="w-4 h-4 text-gold" strokeWidth={1.75} />
                  Mute
                </>
              )}
            </button>
          </div>
        </div>

        {/* CTA below shorts player */}
        <div className="mt-10 max-w-[400px] mx-auto reveal">
          <a
            href={SHORTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="yt-shorts-channel-btn"
            className="group inline-flex w-full items-center justify-between gap-3 px-6 py-5 border border-white/20 text-zinc-200 text-xs uppercase tracking-overline hover:border-gold hover:text-gold transition-colors"
          >
            <span className="inline-flex items-center gap-3">
              <Youtube className="w-5 h-5" strokeWidth={1.5} />
              All Shorts on YouTube
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
